/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { View, Text, Actionable, Carousel } from '@az/starc-ui';
import styles from './styles.module.scss';
import React, { useState } from 'react';
import { ReviewMediaDetailsModal } from '../ReviewImageDetailsModal/ReviewMediaDetailsModal';
import NextImage from '@/components/NextImage';
import { PlayButton } from '@/components/PlayButton/PlayButton';
import { GalleryView } from '../GalleryView/GalleryView';
import { useLabels } from '@/hooks/useLabels';
import { type ReviewPhotoMap } from '@/features/customReviews/hooks/useProductReviewImages';
import { type ReviewType, type ReviewMediaItem } from '../../../interface/types';
import { Review } from '../../Review/Review';
import { useBvPixelTracking } from '../../../hooks/useBvPixelTracking';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';

const labelMap = {
  altImage: 'label_reviews_customerImageAltFallback',
};

type ImageProps = {
  mediaItems: ReviewMediaItem[];
  setSelectedImageIndex: React.Dispatch<React.SetStateAction<number>>;
  setDetailsModalState: React.Dispatch<React.SetStateAction<boolean>>;
};

const Images = ({ mediaItems, setSelectedImageIndex, setDetailsModalState }: ImageProps) => {
  const labels = useLabels(labelMap);

  const previewMediaItems = mediaItems.slice(0, 5);

  const { trackEvent } = useBvPixelTracking();

  return (
    <>
      {previewMediaItems.map((media, index) => {
        const thumbnailUrl = media.type === 'photo' ? media.url : media.thumbnailUrl;
        const isVideo = media.type === 'video';

        return (
          <Actionable
            key={index}
            onClick={() => {
              setSelectedImageIndex(index);
              setDetailsModalState(true);
              trackEvent('Review Images', 'Click', isVideo ? 'Review Video' : 'Review Image');
            }}
          >
            <div className={styles.thumbnailWrapper}>
              <NextImage
                src={thumbnailUrl}
                width={120}
                height={120}
                alt={media.caption || labels.altImage}
                className={styles.images}
              />
              {isVideo && (
                <div className={styles.playButtonOverlay} aria-hidden="true">
                  <PlayButton size={4} />
                </div>
              )}
            </div>
          </Actionable>
        );
      })}
    </>
  );
};

export type PreviewImageProps = {
  mediaItems: ReviewMediaItem[];
  reviews?: ReviewType[] | null | undefined | ReviewType;
  photoMap?: ReviewPhotoMap | null | undefined;
};

const labelsMap = {
  CustomerImages: 'label_Reviews_CustomerImages',
  seeAllImages: 'label_Reviews_See_All_Images',
};

export const PreviewImages = ({ mediaItems, reviews, photoMap }: PreviewImageProps) => {
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
  const [isDetailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const { trackEvent } = useBvPixelTracking();

  const labels = useLabels(labelsMap);

  const handleGalleryModalState: React.Dispatch<React.SetStateAction<boolean>> = (data) => {
    setIsGalleryModalOpen(data);
  };

  const handleDetailsModalState: React.Dispatch<React.SetStateAction<boolean>> = (data) => {
    setDetailsModalOpen(data);
  };
  const enableShowAllImages = mediaItems.length > 5;

  return (
    <View direction={'column'} className={styles.reviewImagesContainer}>
      <View direction={'row'} justify="space-between" align="end">
        <View.Item>
          <Text variant="large-body" className={styles.customerReviewsHeading}>
            {labels.CustomerImages}
          </Text>
        </View.Item>
        {enableShowAllImages ? (
          <Actionable
            onClick={() => {
              handleGalleryModalState(true);
              setSelectedImageIndex(0);
              trackEvent('Review Images', 'Click', 'See All Images');
            }}
          >
            <Text decoration="underline" className={styles.headerRightText}>
              {labels.seeAllImages}
            </Text>
          </Actionable>
        ) : null}
      </View>
      <Carousel className={styles.carousel} hideControls={true}>
        <FlexGrid className={styles.imagesContainer}>
          <Images
            mediaItems={mediaItems}
            setSelectedImageIndex={setSelectedImageIndex}
            setDetailsModalState={handleDetailsModalState}
          />
        </FlexGrid>
      </Carousel>

      {isGalleryModalOpen && (
        <GalleryView
          isGalleryModalOpen={isGalleryModalOpen}
          isReviewDetailsModalOpen={isDetailsModalOpen}
          handleGalleryModalOpen={handleGalleryModalState}
          mediaItems={mediaItems}
          handleReviewDetailsModalOpen={handleDetailsModalState}
          imageIndex={selectedImageIndex}
          setImageIndex={setSelectedImageIndex}
          reviews={reviews}
          photoMap={photoMap}
        />
      )}
      {isDetailsModalOpen && (
        <ReviewMediaDetailsModal
          isReviewDetailsModalOpen={isDetailsModalOpen}
          handleReviewDetailsModalOpen={handleDetailsModalState}
          mediaItems={mediaItems}
          imageIndex={selectedImageIndex}
          setImageIndex={setSelectedImageIndex}
          reviews={reviews}
          photoMap={photoMap}
          renderReview={(reviewData) => <Review review={reviewData} detailsModal={true} />}
        />
      )}
    </View>
  );
};
